// SummaryTable.jsx
import React, { useMemo } from 'react';
import { Card } from 'primereact/card';
import { Fieldset } from 'primereact/fieldset';
import moment from 'moment'; 
import { classNames } from 'primereact/utils'; 

// Importa los iconos de PrimeIcons si no los tienes ya
// import 'primeicons/primeicons.css'; 

import './SummaryTable.scss'; 
// Asegúrate de que tu proyecto PrimeReact esté configurado con PrimeFlex para la cuadrícula
// import 'primeflex/primeflex.css'; 

const SummaryTable = ({ driversData }) => {

    const parseServicesForSummary = (servicesString) => {
        if (!servicesString || servicesString === 'Sin Servicios') {
            return [];
        }

        const serviceEntries = servicesString.split(',');
        let parsed = serviceEntries.map(entry => {
            const parts = entry.split('|');
            if (parts.length < 8) { return null; }
            const id = parts[0];
            const status = parts[4].trim();
            const serviceType = parts[5].trim();
            const startTimeStr = parts[6].trim();
            const endTimeStr = parts[7].trim();
            const dateFormat = 'DD/MM/YYYY HH:mm';
            const startTime = moment(startTimeStr, dateFormat);
            const endTime = moment(endTimeStr, dateFormat);
            if (!startTime.isValid() || !endTime.isValid()) { return null; }
            const startOfDay = startTime.clone().startOf('day');
            if (endTime.isBefore(startTime) && endTime.date() === startTime.date()) { endTime.add(1, 'days'); }
            const startMinutes = moment.duration(startTime.diff(startOfDay)).asMinutes();
            const endMinutes = moment.duration(endTime.diff(startOfDay)).asMinutes();
            if (isNaN(startMinutes) || isNaN(endMinutes) || endMinutes <= startMinutes) { return null; }
            return { id: id, start: startMinutes, end: endMinutes, isOverlap: false, status: status, serviceType: serviceType };
        }).filter(Boolean); 
        parsed.sort((a, b) => a.start - b.start);
        let currentActiveServiceEnd = -1;
        parsed = parsed.map(service => {
            let isCurrentServiceOverlapping = false;
            if (service.start < currentActiveServiceEnd) { isCurrentServiceOverlapping = true; }
            currentActiveServiceEnd = Math.max(currentActiveServiceEnd, service.end);
            return { ...service, isOverlap: isCurrentServiceOverlapping };
        });
        return parsed;
    };


    const summaryMetrics = useMemo(() => {
        if (!driversData || driversData.length === 0) {
            return {
                totalOperadores: 0, totalErrores: 0, totalServicios: 0, totalServiciosEmpresariales: 0,
                totalEspeciales: 0, totalOperacion: 0, totalTaller: 0, totalKmRuta: 0,
                serviciosConfirmados: 0, estructurasSinTipoUnidad: 0, operadoresSinServicios: 0,
                operadoresBaja: 0, operadoresConDescanso: 0, operadoresConPermisos: 0,
                operadoresConIncapacidad: 0, operadoresConVacaciones: 0
            };
        }

        let totalOperadores = 0; let totalErrores = 0; let totalServicios = 0; let totalServiciosEmpresariales = 0;
        let totalEspeciales = 0; let totalOperacion = 0; let totalTaller = 0; let totalKmRuta = 0;
        let serviciosConfirmados = 0; let estructurasSinTipoUnidad = 0; let operadoresSinServicios = 0;
        let operadoresBaja = 0; let operadoresConDescanso = 0; let operadoresConPermisos = 0;
        let operadoresConIncapacidad = 0; let operadoresConVacaciones = 0;

        const processedOperators = new Set(); 

        driversData.forEach(driver => {
            if (driver.ID && !processedOperators.has(driver.ID)) {
                totalOperadores++;
                processedOperators.add(driver.ID);

                if (driver.ESTATUS === 'Baja') { operadoresBaja++;
                } else if (driver.ESTATUS === 'Descanso') { operadoresConDescanso++;
                } else if (driver.ESTATUS === 'Permiso') { operadoresConPermisos++;
                } else if (driver.ESTATUS === 'Incapacitado') { operadoresConIncapacidad++;
                } else if (driver.ESTATUS === 'Vacaciones') { operadoresConVacaciones++; }
            }

            if (typeof driver.KM === 'number') { totalKmRuta += driver.KM;
            } else if (typeof driver.KM === 'string' && !isNaN(parseFloat(driver.KM))) { totalKmRuta += parseFloat(driver.KM); }
            if (typeof driver.SERVICIOS === 'number') { totalServicios += driver.SERVICIOS;
            } else if (typeof driver.SERVICIOS === 'string' && !isNaN(parseInt(driver.SERVICIOS))) { totalServicios += parseInt(driver.SERVICIOS); }
            if (driver.SERVICIOS === 0 || driver.SERVICIOS === '0' || driver.SERVICIOS_CONCATENADOS === 'Sin Servicios') { operadoresSinServicios++; }

            const driverServices = parseServicesForSummary(driver.SERVICIOS_CONCATENADOS);
            driverServices.forEach(service => {
                if (service.isOverlap) { totalErrores++; }
                if (service.status === 'CONFIRMADA') { serviciosConfirmados++; }
                if (service.serviceType === 'EMPRESARIAL') { totalServiciosEmpresariales++; }
                if (service.serviceType === 'ESPECIAL') { totalEspeciales++; }
                if (service.serviceType === 'ADMINISTRATIVO') { totalOperacion++; }
                if (service.serviceType === 'TALLER') { totalTaller++; }
            });
        });

        return {
            totalOperadores, totalErrores, totalServicios, totalServiciosEmpresariales,
            totalEspeciales, totalOperacion, totalTaller, totalKmRuta: totalKmRuta.toFixed(2),
            serviciosConfirmados, estructurasSinTipoUnidad, operadoresSinServicios,
            operadoresBaja, operadoresConDescanso, operadoresConPermisos,
            operadoresConIncapacidad, operadoresConVacaciones
        };
    }, [driversData]);

    const getValueColorClass = (label) => {
        if (label.includes('Errores') || label.includes('Baja') || label.includes('Sin Servicios')) {
            return 'metric-value-danger';
        }
        if (label.includes('Confirmados') || label.includes('Servicios') && !label.includes('Sin')) {
            return 'metric-value-success';
        }
        return 'metric-value-neutral';
    };

    const MetricIndicator = ({ label, value, iconClass, colorClass }) => (
        <div className="metric-item"> {/* Esto ya no es una Card individual, es solo un div */}
            <span className="metric-label">
                {iconClass && <i className={classNames(iconClass, 'mr-2')} />}
                {label}:
            </span>
            <span className={classNames("metric-value", colorClass)}>{value}</span>
        </div>
    );

    const cardHeaderTemplate = (title) => (
        <div className="category-card-header">
            <h4>{title}</h4>
        </div>
    );


    return (
        <div className="card summary-dashboard"> 
            <h3 className="summary-title">Resumen de Operación</h3>
            
            {/* Contenedor Flex para las Cards de categoría */}
            <div className="category-cards-container">
                {/* Card para Indicadores Generales */}
                <Card title={cardHeaderTemplate("Generales")} className="category-card">
                    <div className="p-grid p-nogutter category-grid"> {/* Grid dentro de la Card de categoría */}
                        <div className="p-col-12 p-md-6 p-lg-4">
                            <MetricIndicator label="Total Operadores" value={summaryMetrics.totalOperadores} iconClass="pi pi-users" colorClass={getValueColorClass('Total Operadores')}/>
                        </div>
                        <div className="p-col-12 p-md-6 p-lg-4">
                            <MetricIndicator label="Total Servicios" value={summaryMetrics.totalServicios} iconClass="pi pi-briefcase" colorClass={getValueColorClass('Total Servicios')}/>
                        </div>
                        <div className="p-col-12 p-md-6 p-lg-4">
                            <MetricIndicator label="Servicios Confirmados (100%)" value={summaryMetrics.serviciosConfirmados} iconClass="pi pi-check-circle" colorClass={getValueColorClass('Servicios Confirmados')}/>
                        </div>
                        <div className="p-col-12 p-md-6 p-lg-4">
                            <MetricIndicator label="Total Km Ruta" value={summaryMetrics.totalKmRuta} iconClass="pi pi-map" colorClass={getValueColorClass('Total Km Ruta')}/>
                        </div>
                        <div className="p-col-12 p-md-6 p-lg-4">
                            <MetricIndicator label="Total Errores (Solapamientos)" value={summaryMetrics.totalErrores} iconClass="pi pi-exclamation-triangle" colorClass={getValueColorClass('Total Errores')}/>
                        </div>
                        <div className="p-col-12 p-md-6 p-lg-4">
                            <MetricIndicator label="Operadores Sin Servicios" value={summaryMetrics.operadoresSinServicios} iconClass="pi pi-user-minus" colorClass={getValueColorClass('Operadores Sin Servicios')}/>
                        </div>
                    </div>
                </Card>

                {/* Card para Indicadores por Tipo de Servicio */}
                <Card title={cardHeaderTemplate("Por Tipo de Servicio")} className="category-card">
                    <div className="p-grid p-nogutter category-grid">
                        <div className="p-col-12 p-md-6 p-lg-4">
                            <MetricIndicator label="Servicios Empresariales" value={summaryMetrics.totalServiciosEmpresariales} iconClass="pi pi-building" colorClass={getValueColorClass('Servicios Empresariales')}/>
                        </div>
                        <div className="p-col-12 p-md-6 p-lg-4">
                            <MetricIndicator label="Servicios Especiales" value={summaryMetrics.totalEspeciales} iconClass="pi pi-star" colorClass={getValueColorClass('Servicios Especiales')}/>
                        </div>
                        <div className="p-col-12 p-md-6 p-lg-4">
                            <MetricIndicator label="Servicios Operación (Administrativos)" value={summaryMetrics.totalOperacion} iconClass="pi pi-cog" colorClass={getValueColorClass('Servicios Operación')}/>
                        </div>
                        <div className="p-col-12 p-md-6 p-lg-4">
                            <MetricIndicator label="Servicios Taller" value={summaryMetrics.totalTaller} iconClass="pi pi-wrench" colorClass={getValueColorClass('Servicios Taller')}/>
                        </div>
                    </div>
                </Card>

                {/* Card para Indicadores por Estatus de Operador */}
                <Card title={cardHeaderTemplate("Estatus de Operadores")} className="category-card">
                    <div className="p-grid p-nogutter category-grid">
                        <div className="p-col-12 p-md-6 p-lg-4">
                            <MetricIndicator label="Operadores Baja" value={summaryMetrics.operadoresBaja} iconClass="pi pi-user-minus" colorClass={getValueColorClass('Operadores Baja')}/>
                        </div>
                        <div className="p-col-12 p-md-6 p-lg-4">
                            <MetricIndicator label="Operadores con Descanso" value={summaryMetrics.operadoresConDescanso} iconClass="pi pi-sun" colorClass={getValueColorClass('Operadores con Descanso')}/>
                        </div>
                        <div className="p-col-12 p-md-6 p-lg-4">
                            <MetricIndicator label="Operadores con Permisos" value={summaryMetrics.operadoresConPermisos} iconClass="pi pi-file-o" colorClass={getValueColorClass('Operadores con Permisos')}/>
                        </div>
                        <div className="p-col-12 p-md-6 p-lg-4">
                            <MetricIndicator label="Operadores con Incapacidad" value={summaryMetrics.operadoresConIncapacidad} iconClass="pi pi-hospital" colorClass={getValueColorClass('Operadores con Incapacidad')}/>
                        </div>
                        <div className="p-col-12 p-md-6 p-lg-4">
                            <MetricIndicator label="Operadores con Vacaciones" value={summaryMetrics.operadoresConVacaciones} iconClass="pi pi-send" colorClass={getValueColorClass('Operadores con Vacaciones')}/>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default SummaryTable;